import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import Brand from "@/components/site/Brand";

const SERVICE_LINKS = [
  { label: "Business Consulting", to: "/services/security-strategy" },
  { label: "Deployment Services", to: "/services/sase" },
  { label: "Managed Services (MSSP)", to: "/services/mssp" },
  { label: "Testing Services", to: "/services/vapt" },
];
const USEFUL_LINKS = [
  { label: "About Us", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "Resources", to: "/insights" },
  { label: "Contact Us", to: "/contact" },
];
const LEGAL = [
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Cookies", to: "/cookies" },
  { label: "Disclaimer", to: "/disclaimer" },
];

function FooterLink({ to, children, external }) {
  const cls =
    "group inline-flex items-center gap-1.5 text-[14px] text-[#0B0B10] hover:text-[#5B2A86] transition-colors";
  const arrow = (
    <span className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#F5B841] text-[13px]">
      →
    </span>
  );
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
        {arrow}
        <span>{children}</span>
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {arrow}
      <span>{children}</span>
    </Link>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#FAFAFA] theme-editorial-global">
      {/* Top editorial CTA band (white) */}
      <div className="bg-white border-b border-[rgba(11,11,16,0.08)]">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <h2
            className="md:col-span-8 font-editorial-italic text-[#0B0B10] leading-[1.02] tracking-[-0.02em]"
            style={{ fontStyle: "italic", fontSize: "clamp(2rem, 4.4vw, 4rem)", fontFamily: "Fraunces, Georgia, serif", fontWeight: 400 }}
          >
            Ready to elevate your cyber resilience?
          </h2>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="group inline-flex items-baseline gap-2 text-[16px] md:text-[18px] font-medium text-[#5B2A86]"
              style={{ minHeight: 44 }}
            >
              <span className="relative">
                Talk to a Security Expert
                <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-[#F5B841] group-hover:w-full transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              </span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 4-column grid (light gray) */}
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-12 md:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Column 1 — Company */}
        <div>
          <div className="mb-5">
            <Brand variant="full" height={48} imgClassName="h-[48px]" light />
          </div>
          <p className="text-[14px] leading-[1.55] text-[#4A4A55] max-w-[280px]">
            RockLadder is a next-generation cybersecurity partner.
            Resilience for a changing world.
          </p>
          <div className="mt-6 font-editorial-mono text-[10.5px] text-[#8A8A8A] flex items-center gap-2">
            <span>RL HQ</span>
            <span className="h-1 w-1 rounded-full bg-[#5B2A86]" />
            <span>NOIDA / INDIA</span>
          </div>
        </div>

        {/* Column 2 — Services */}
        <div>
          <div className="font-editorial-mono text-[10.5px] text-[#8A8A8A] mb-5">
            EXPLORE OUR SERVICES
          </div>
          <ul className="space-y-3">
            {SERVICE_LINKS.map((l) => (
              <li key={l.label}>
                <FooterLink to={l.to}>{l.label}</FooterLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3 — Useful Links */}
        <div>
          <div className="font-editorial-mono text-[10.5px] text-[#8A8A8A] mb-5">
            USEFUL LINKS
          </div>
          <ul className="space-y-3">
            {USEFUL_LINKS.map((l) => (
              <li key={l.label}>
                <FooterLink to={l.to}>{l.label}</FooterLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4 — Get In Touch */}
        <div>
          <div className="font-editorial-mono text-[10.5px] text-[#8A8A8A] mb-5">
            GET IN TOUCH
          </div>
          <ul className="space-y-3">
            <li>
              <a
                href="mailto:contact@rockladder.com"
                className="text-[14px] text-[#0B0B10] hover:text-[#5B2A86] transition-colors"
              >
                contact@rockladder.com
              </a>
            </li>
            <li>
              <a
                href="tel:+911240000000"
                className="text-[14px] text-[#0B0B10] hover:text-[#5B2A86] transition-colors"
              >
                +91 124 XXX XXXX
              </a>
            </li>
          </ul>
          <div className="mt-8">
            <div className="font-editorial-mono text-[10.5px] text-[#8A8A8A] mb-3">
              FOLLOW US
            </div>
            <a
              href="https://www.linkedin.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="inline-flex items-center justify-center h-11 w-11 text-[#0B0B10] hover:text-[#5B2A86] transition-colors"
              data-testid="social-linkedin"
            >
              <Linkedin className="h-5 w-5" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[rgba(11,11,16,0.08)]">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-[#8A8A8A]">
          <div className="font-editorial-mono text-[10.5px] tracking-[0.15em]">
            © 2026 ROCKLADDER TECHNOLOGIES. ALL RIGHTS RESERVED.
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-center">
            {LEGAL.map((l, i) => (
              <span key={l.label} className="flex items-center gap-3">
                <Link to={l.to} className="hover:text-[#5B2A86] transition-colors" data-testid={`legal-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
                {i < LEGAL.length - 1 && (
                  <span className="text-[#0B0B10]/25">·</span>
                )}
              </span>
            ))}
          </div>
          <div className="text-[12px] md:text-right">
            Website Powered by{" "}
            <a
              href="https://techbook.co.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0B0B10] hover:text-[#F5B841] transition-colors"
              data-testid="techbook-link"
            >
              Techbook Technologies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
